import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import FullscreenControl from "../components/FullscreenControl";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import L from "leaflet";
import { getReadingsForMap, getEmployees, getSettings, updateSetting } from "../services/api";
import { cloudinaryThumb, cloudinaryPreview } from "../services/cloudinary";
import { MapPin, CalendarDays, Users, Target, Pencil, Check, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const DEFAULT_GOAL = 7000;

const ROLE_LABEL = {
  lector: "Auditor",
  "lector-plus": "Auditor Plus",
  field_agent: "Auditor Plus",
};
const ROLE_COLOR = {
  lector: { background: "var(--primary-bg)", color: "#D4620D" },
  "lector-plus": { background: "#fdf4ff", color: "#9333ea" },
  field_agent: { background: "#fdf4ff", color: "#9333ea" },
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});
const dotIcon = L.divIcon({
  className: "",
  html: '<div style="width:10px;height:10px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

function clusterIcon(cluster) {
  const n = cluster.getChildCount();
  const size = n < 10 ? 28 : n < 100 ? 34 : 40;
  const fs = n < 10 ? 11 : 13;
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#2563eb;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${fs}px;border:2px solid #fff;box-shadow:0 2px 8px rgba(37,99,235,0.35);font-family:Montserrat,sans-serif">${n}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === 'admin-plus';
  const [readings, setReadings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyGoal, setMonthlyGoal] = useState(DEFAULT_GOAL);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [statsTotal, setStatsTotal]     = useState(null); // total en BD
  const [statsMonth, setStatsMonth]     = useState(null); // este mes
  const [statsToday, setStatsToday]     = useState(null); // hoy
  const [hasMore, setHasMore]           = useState(false);
  const [loadingAll, setLoadingAll]     = useState(false);

  const MAP_INITIAL_LIMIT = 5000;

  useEffect(() => {
    (async () => {
      try {
        const [mapRes, e, settings] = await Promise.all([
          getReadingsForMap({ limit: MAP_INITIAL_LIMIT }),
          getEmployees(),
          getSettings(),
        ]);
        if (settings?.monthly_goal) setMonthlyGoal(parseInt(settings.monthly_goal, 10));
        const rows = mapRes.data ?? mapRes;
        setReadings(rows);
        setStatsTotal(mapRes.total ?? rows.length);
        setStatsMonth(mapRes.total_month ?? null);
        setStatsToday(mapRes.total_today ?? null);
        setHasMore(mapRes.has_more ?? false);
        setEmployees(e);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function loadAllReadings() {
    setLoadingAll(true);
    try {
      const mapRes = await getReadingsForMap();
      setReadings(mapRes.data ?? mapRes);
      setHasMore(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAll(false);
    }
  }

  if (loading) return <div style={s.loading}>Cargando...</div>;

  // Usar totales reales del backend (no limitados a los 5000 del mapa)
  const totalMedidores = statsTotal ?? readings.length;
  const thisMonth      = statsMonth ?? 0;
  const todayCount     = statsToday ?? 0;
  const progress  = Math.min(100, (thisMonth / monthlyGoal) * 100);
  const remaining = Math.max(0, monthlyGoal - thisMonth);
  const active = employees.filter((e) => e.active && (e.role === "lector-plus" || e.role === "lector"));

  const mapCenter = [-34.6037, -58.3816];

  return (
    <div>
      <h1 style={s.title}>Dashboard</h1>

      {/* ── Stat cards ── */}
      <div style={s.cards}>
        <StatCard
          label="Medidores ubicados"
          value={totalMedidores.toLocaleString("es-AR")}
          Icon={MapPin}
          color="#D4620D"
        />
        <StatCard
          label="Ubicados hoy"
          value={todayCount.toLocaleString("es-AR")}
          Icon={CalendarDays}
          color="#16a34a"
        />
        <StatCard
          label="Auditores activos"
          value={active.length}
          Icon={Users}
          color="#9333ea"
        />

        {/* Monthly goal with progress bar */}
        <div style={{ ...s.card, borderTop: "3px solid #ea580c" }}>
          {/* Icon + bar on the same row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div style={{ color: "#ea580c", flexShrink: 0 }}>
              <Target size={22} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={s.barTrack}>
                <div
                  style={{
                    ...s.barFill,
                    width: `${progress}%`,
                    background: progress >= 100 ? "#16a34a" : "#ea580c",
                  }}
                />
              </div>
            </div>
            <span
              style={{
                border: "1px solid var(--border)",
                fontSize: 13,
                fontWeight: 700,
                color: progress >= 100 ? "#16a34a" : "#ea580c",
                flexShrink: 0,
              }}
            >
              {progress.toFixed(1)}%
            </span>
          </div>
          {/* Value */}
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 4,
              marginBottom: 2,
            }}
          >
            <span style={{ ...s.cardValue, color: "#ea580c" }}>
              {thisMonth.toLocaleString("es-AR")}
            </span>
            <span style={{ fontSize: 13, color: "var(--text-2)" }}>
              / {monthlyGoal.toLocaleString("es-AR")}
            </span>
          </div>
          {/* Label + remaining / edit */}
          {editingGoal ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
              <input
                autoFocus
                type="number"
                min={1}
                value={goalInput}
                onChange={(e) => setGoalInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = parseInt(goalInput, 10);
                    if (v > 0) { setMonthlyGoal(v); updateSetting("monthly_goal", v).catch(() => {}); }
                    setEditingGoal(false);
                  }
                  if (e.key === "Escape") setEditingGoal(false);
                }}
                style={s.goalInput}
              />
              <button style={s.goalBtn} onClick={() => {
                const v = parseInt(goalInput, 10);
                if (v > 0) { setMonthlyGoal(v); updateSetting("monthly_goal", v).catch(() => {}); }
                setEditingGoal(false);
              }}><Check size={13} /></button>
              <button style={{ ...s.goalBtn, background: "var(--bg-subtle)", color: "var(--text-2)" }} onClick={() => setEditingGoal(false)}>
                <X size={13} />
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={s.cardLabel}>Objetivo mensual</span>
                {isAdminPlus && (
                  <button style={s.pencilBtn} onClick={() => { setGoalInput(monthlyGoal.toString()); setEditingGoal(true); }}>
                    <Pencil size={11} />
                  </button>
                )}
              </div>
              <span style={{ fontSize: 13, color: remaining === 0 ? "#16a34a" : "#9a9a93" }}>
                {remaining === 0 ? "✓ Meta" : `Faltan ${remaining.toLocaleString("es-AR")}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom row ── */}
      <div style={s.bottom}>
        {/* Map */}
        <div style={s.box}>
          <div style={s.boxHeader}>
            <MapPin size={14} color="#6b6b66" />
            <span style={s.boxTitle}>Medidores ubicados</span>
            <span style={s.boxCount}>
              {readings.length.toLocaleString("es-AR")}
              {hasMore && statsTotal && (
                <span style={{ color: "#94a3b8", fontWeight: 500, fontSize: 11 }}>
                  {" "}de {statsTotal.toLocaleString("es-AR")}
                </span>
              )}
            </span>
            {hasMore && (
              <button
                style={s.loadAllBtn}
                onClick={loadAllReadings}
                disabled={loadingAll}
              >
                {loadingAll ? "Cargando..." : "Cargar todos"}
              </button>
            )}
          </div>
          <div style={s.mapWrap}>
            <MapContainer
              center={mapCenter}
              zoom={12}
              style={{ width: "100%", height: "100%" }}
              preferCanvas
              scrollWheelZoom
            >
              <FullscreenControl />
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution="&copy; OpenStreetMap &copy; CARTO"
              />
              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={45}
                showCoverageOnHover={false}
                iconCreateFunction={clusterIcon}
              >
                {readings.map((r) => {
                  const lat = parseFloat(r.latitude);
                  const lng = parseFloat(r.longitude);
                  if (isNaN(lat) || isNaN(lng)) return null;
                  return (
                    <Marker key={r.id} position={[lat, lng]} icon={dotIcon}>
                      <Popup minWidth={200} maxWidth={240}>
                        <div style={{ fontFamily: "Montserrat, sans-serif" }}>
                          {r.image_url && (
                            <img
                              src={cloudinaryThumb(r.image_url, 200, 130)}
                              alt=""
                              loading="lazy"
                              onClick={() => setLightbox(cloudinaryPreview(r.image_url, 1400))}
                              style={{ width: "100%", borderRadius: 6, marginBottom: 8, cursor: "zoom-in", display: "block" }}
                            />
                          )}
                          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{r.user_name || "—"}</div>
                          {r.meter_code && (
                            <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 2 }}>
                              Medidor: <strong>{r.meter_code}</strong>
                            </div>
                          )}
                          <div style={{ fontSize: 13, color: "var(--text-2)" }}>
                            {format(new Date(r.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        </div>

        {/* Active collaborators */}
        <div style={s.box}>
          <div style={s.boxHeader}>
            <Users size={14} color="#6b6b66" />
            <span style={s.boxTitle}>Auditores activos</span>
            <span style={s.boxCount}>{active.length}</span>
          </div>
          <div style={s.empList}>
            {active.length === 0 && (
              <div style={s.emptyState}>No hay auditores activos</div>
            )}
            {active.map((e) => {
              const online = !!e.is_online;
              return (
                <div key={e.id} style={s.empRow}>
                  {/* Status dot */}
                  <div
                    style={{
                      ...s.statusDot,
                      background: online ? "#16a34a" : "#dc2626",
                    }}
                    title={online ? "Conectado" : "Desconectado"}
                  />
                  <div style={s.empAvatar}>
                    {e.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.empName}>{e.name}</div>
                    <div style={s.empZone}>{e.zone || "—"}</div>
                  </div>
                  <div style={s.empStats}>
                    <span style={s.empStatVal}>
                      {Number(e.today_readings).toLocaleString("es-AR")}
                    </span>
                    <span style={s.empStatLbl}>hoy</span>
                    <span style={{ ...s.empStatVal, marginLeft: 10 }}>
                      {Number(e.total_readings).toLocaleString("es-AR")}
                    </span>
                    <span style={s.empStatLbl}>total</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Lightbox foto */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, cursor: "zoom-out",
          }}
        >
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: "absolute", top: 18, right: 18,
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: "50%", width: 38, height: 38,
              color: "#fff", fontSize: 20, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ×
          </button>
          <img
            src={lightbox}
            alt=""
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 10, boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
          />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, Icon, color }) {
  return (
    <div style={{ ...s.card, borderTop: `3px solid ${color}` }}>
      <div style={{ ...s.cardIcon, color }}>
        <Icon size={22} />
      </div>
      <div style={{ ...s.cardValue, color }}>{value}</div>
      <div style={s.cardLabel}>{label}</div>
    </div>
  );
}

const s = {
  loading: { padding: 60, textAlign: "center", color: "var(--text-2)" },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "var(--text)",
    margin: "0 0 24px",
  },

  // Stat cards
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
    marginBottom: 20,
  },
  card: {
    background: "var(--bg-card)",
    borderRadius: 14,
    padding: 22,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  cardIcon: { marginBottom: 10 },
  cardValue: { fontSize: 32, fontWeight: 700, marginBottom: 2 },
  cardLabel: {
    fontSize: 13,
    color: "var(--text-2)",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  // Progress bar
  barTrack: {
    height: 8,
    background: "var(--bg-subtle)",
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 6,
  },
  barFill: { height: "100%", borderRadius: 4, transition: "width 0.8s ease" },
  barFooter: {
    display: "flex",
    justifyContent: "space-between",fontSize: 13,
    color: "var(--text-2)",
  },

  // Bottom row
  bottom: { display: "grid", gridTemplateColumns: "60% 1fr", gap: 16 },
  box: {
    borderRadius: 14,
    padding: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
  },
  boxHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 14,
  },
  boxTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    flex: 1,
  },
  boxCount: {
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    borderRadius: 10,
    padding: "2px 9px", fontSize: 13,
    fontWeight: 700,
  },
  loadAllBtn: {
    padding: "3px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
    background: "#fff", color: "#475569", fontSize: 11, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit", marginLeft: 6,
    transition: "opacity .15s",
  },

  // Map
  mapWrap: { flex: 1, minHeight: 400, borderRadius: 10, overflow: "hidden" },

  // Employee list
  empList: { flex: 1, overflowY: "auto", maxHeight: 460 },
  emptyState: {
    textAlign: "center",
    padding: 40,
    color: "var(--text-2)",fontSize: 13,
  },
  empRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 2px",
    borderBottom: "1px solid var(--border)",
  },
  empAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#e6e6e2",
    color: "var(--text-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,fontSize: 14,
    flexShrink: 0,
  },
  empName: { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  empZone: { fontSize: 13, color: "var(--text-2)", marginTop: 1 },
  roleBadge: {
    padding: "3px 8px",
    borderRadius: 6,fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  statusDot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  empStats: { display: "flex", alignItems: "baseline", gap: 2, flexShrink: 0 },
  empStatVal: { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  empStatLbl: { fontSize: 13, color: "var(--text-2)", marginLeft: 1 },

  // Goal edit
  pencilBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "var(--text-2)", padding: 2, display: "flex", alignItems: "center",
    borderRadius: 4,
  },
  goalInput: {
    flex: 1, padding: "4px 8px", borderRadius: 6,
    border: "1px solid var(--border)",

    fontSize: 13, fontWeight: 700, color: "#ea580c", outline: "none",
    fontFamily: "Montserrat, sans-serif", width: 0,
  },
  goalBtn: {
    background: "#ea580c", border: "none", borderRadius: 6, cursor: "pointer",
    color: "#fff", padding: "4px 7px", display: "flex", alignItems: "center",
  },
};
